import React, { Component, createRef } from 'react';

import { ANALYIS_CHART_DATA } from 'data/static';
import Highcharts from 'highcharts';
import HighchartsReact, { HighchartsReactRefObject } from 'highcharts-react-official';
import { translate } from 'utils/translate';

interface ProfileHistogramChartProps {
    data: {
        data: number[][];
        plotLines: [string, number][];
    };
    for: string;
    plotLineValues: boolean;
}

interface ProfileHistogramChartState {
    options: any;
    chart: any;
    showHistogram: boolean;
    loading: boolean;
    histogram: Highcharts.SeriesOptionsType | null;
    plotLineValues: boolean;
}

class ProfileHistogramChart extends Component<ProfileHistogramChartProps, ProfileHistogramChartState> {
    private chartRef = React.createRef<HighchartsReactRefObject>();

    constructor(props: ProfileHistogramChartProps) {
        super(props);
        this.chartRef = createRef();
        this.state = {
            options: null,
            chart: null,
            showHistogram: false,
            loading: false,
            histogram: null,
            plotLineValues: props.plotLineValues,
        };
    }

    componentDidMount() {
        this.createChart(true);
    }

    static getDerivedStateFromProps(props: ProfileHistogramChartProps, state: ProfileHistogramChartState) {
        if (props.plotLineValues !== state.plotLineValues) {
            return { plotLineValues: props.plotLineValues };
        }

        return null;
    }

    componentDidUpdate(prevProps: ProfileHistogramChartProps, prevState: ProfileHistogramChartState) {
        if (prevState.plotLineValues !== this.state.plotLineValues) {
            this.createChart(false);
        }
    }

    createChart = (init: boolean) => {
        const direction = ANALYIS_CHART_DATA[this.props.for].direction;
        const roundDeg = ANALYIS_CHART_DATA[this.props.for].roundDeg;
        const decimals = ANALYIS_CHART_DATA[this.props.for].decimals;
        const { maxY, minY } = this.findMinMaxY();

        const tickInterval = this.calculateTickInterval(minY, maxY);

        this.setState({
            options: {
                chart: {
                    type: 'column',
                    events: {
                        render: function () {
                            function drawRectangles(chart) {
                                const xAxis = chart.xAxis[0];
                                const height = 10;

                                chart.renderer
                                    .rect(
                                        xAxis.left,
                                        chart.plotTop + chart.plotHeight,
                                        xAxis.toPixels(0) - xAxis.left,
                                        height
                                    )
                                    .attr({
                                        fill: 'red',
                                        opacity: 0.2,
                                    })
                                    .add();

                                let positiveRectStart = xAxis.toPixels(0);
                                if (positiveRectStart < 1 || positiveRectStart - xAxis.left < 0) {
                                    positiveRectStart = xAxis.left;
                                }

                                chart.renderer
                                    .rect(
                                        positiveRectStart,
                                        chart.plotTop + chart.plotHeight,
                                        positiveRectStart - xAxis.left + xAxis.width,
                                        height
                                    )
                                    .attr({
                                        fill: 'green',
                                        opacity: 0.2,
                                    })
                                    .add();
                            }

                            if (direction) {
                                drawRectangles(this);
                            }
                        },
                    },
                },
                title: {
                    text: ANALYIS_CHART_DATA[this.props.for].title,
                    style: { fontSize: '1rem', color: '#000000', fontWeight: '500' },
                },

                legend: {
                    enabled: false,
                },
                xAxis: {
                    title: { text: ANALYIS_CHART_DATA[this.props.for].yLabel },
                    type: 'linear',
                    labels: {
                        style: { fontSize: '10px' },
                        formatter: function () {
                            if (roundDeg) {
                                if (this.value < 0) {
                                    return ((this.value + 360) % 360).toFixed(decimals);
                                }
                                return this.value.toFixed(1);
                            } else if (direction) {
                                return `${this.value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')} ${
                                    this.value == 0 ? '' : this.value > 0 ? 'S' : 'P'
                                }`;
                            }
                            return this.value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
                        },
                    },
                    plotLines: this.buildPlotLines(),
                },
                additionalData: ANALYIS_CHART_DATA[this.props.for],
                yAxis: {
                    tickInterval: tickInterval,
                    max: maxY % tickInterval === 0 ? maxY + tickInterval : maxY - (maxY % tickInterval) + tickInterval,
                    title: { text: 'No. of Transits' },
                    labels: {
                        style: {
                            fontSize: '10px',
                        },
                    },
                },
                credits: { enabled: false },
                tooltip: {
                    shared: false,
                    positioner: function (labelWidth, labelHeight, point) {
                        let tooltipX, tooltipY;

                        // Default position: tooltip to the right of the mouse pointer
                        tooltipX = point.plotX + this.chart.plotLeft + 10;

                        // If the tooltip would go outside the right boundary, position it to the left of the mouse pointer
                        if (tooltipX + labelWidth > this.chart.plotLeft + this.chart.plotWidth) {
                            tooltipX = point.plotX + this.chart.plotLeft - labelWidth - 10;
                        }

                        // If the tooltip would go outside the left boundary, position it to the right of the mouse pointer
                        if (tooltipX < this.chart.plotLeft) {
                            tooltipX = point.plotX + this.chart.plotLeft + 10;
                        }

                        // Center the tooltip vertically relative to the whisker box
                        tooltipY = point.plotY + this.chart.plotTop + point.h / 2 - labelHeight / 2;

                        // Prevent the tooltip from going outside the bottom boundary
                        if (tooltipY + labelHeight > this.chart.plotTop + this.chart.plotHeight) {
                            tooltipY = this.chart.plotTop + this.chart.plotHeight - labelHeight - 10;
                        }

                        // Prevent the tooltip from going outside the top boundary
                        if (tooltipY < this.chart.plotTop) {
                            tooltipY = this.chart.plotTop + 10;
                        }

                        return {
                            x: tooltipX,
                            y: tooltipY,
                        };
                    },
                    style: {
                        opacity: 0.7,
                        fontSize: '12px',
                        useHTML: true,
                    },
                },
                plotOptions: {
                    column: { pointPadding: 0, groupPadding: 0.01, borderWidth: 0 },
                },
                series: [
                    {
                        zIndex: 1,
                        color: 'rgba(44,175,254 , 0.7)',
                        name: 'No. of Transits',
                        data: this.props.data[0].data.transit_ids
                            .map((x) => {
                                const data = this.props.data[0].data.col_edges;
                                let downEdge = null;
                                let topEdge = null;

                                for (let i = 0; i < data.length; i++) {
                                    if (data[i] <= parseFloat(x.key)) {
                                        downEdge = data[i]; // Largest number <= value
                                    }
                                    if (data[i] >= parseFloat(x.key) && topEdge === null) {
                                        topEdge = data[i]; // Smallest number >= value (first occurrence)
                                    }
                                }
                                return {
                                    x: parseFloat(x.key),
                                    y: x.value.length,
                                    t: x.value,
                                    downEdge,
                                    topEdge,
                                    l: ANALYIS_CHART_DATA[this.props.for].ToolTipLabel,
                                    u: ANALYIS_CHART_DATA[this.props.for].unit,
                                };
                            })
                            .filter((x) => {
                                const maxRecord = this.props.data[0].plotLines.find((y) => {
                                    return y[0] === 'max';
                                })[1];
                                const minRecord = this.props.data[0].plotLines.find((y) => {
                                    return y[0] === 'min';
                                })[1];

                                return x.topEdge > minRecord && x.downEdge < maxRecord;
                            }),
                        tooltip: {
                            zIndex: 6,
                            headerFormat: '',
                            pointFormatter: function () {
                                const data = this.series.chart.options.series[0].data[this.index];
                                const count = data.t.length;

                                return ` 
                    <div>${data.l}: ${data.downEdge.toFixed(this.series.chart.options.additionalData.decimals)} ${
                                    data.u
                                } to ${data.topEdge.toFixed(this.series.chart.options.additionalData.decimals)} ${
                                    data.u
                                }</div></br>
                    <div >
                      <div>${translate('Transits')}: ${count}</div></br>
                    </div>`;
                            },
                        },
                    },
                    {
                        type: 'polygon',
                        data: init
                            ? this.createPolygon(
                                  maxY % tickInterval === 0
                                      ? maxY + tickInterval
                                      : maxY - (maxY % tickInterval) + tickInterval
                              )
                            : this.state.options.series[1].data,
                        color: 'rgba(126, 159 ,189, 0.5)',
                        enableMouseTracking: false,
                        marker: { enabled: false },
                        zIndex: 0,
                    },
                ],
            },
        });
    };

    calculateTickInterval = (min: number, max: number): number => {
        const range = max - min;
        const roughInterval = range / 5;

        const power = Math.pow(10, Math.floor(Math.log10(roughInterval)));

        let tickInterval;
        if (roughInterval >= 5 * power) {
            tickInterval = 10 * power;
        } else if (roughInterval >= 2 * power) {
            tickInterval = 5 * power;
        } else {
            tickInterval = 2 * power;
        }

        return tickInterval > range ? range : tickInterval;
    };

    findMinMaxY = (): { minY: number; maxY: number } => {
        const data = this.props.data[0].data.transit_ids.map((x) => x.value.length);
        const minY = Math.min(...data);
        const maxY = Math.max(...data);
        return { minY, maxY };
    };

    buildPlotLines = () => {
        const decimals = ANALYIS_CHART_DATA[this.props.for].decimals;
        const unit = ANALYIS_CHART_DATA[this.props.for].unit;

        // Remove duplicates
        const uniquePlotLines = Array.from(new Map(this.props.data[0].plotLines.map((p) => [p[1], p])).values());

        // Sort by numeric value
        //@ts-ignore
        const sortedPlotLines = [...uniquePlotLines].sort((a, b) => a[1] - b[1]);

        return [
            ANALYIS_CHART_DATA[this.props.for].direction
                ? {
                      zIndex: 5,
                      value: 0,
                      color: 'black',
                      width: 1,
                      label: {
                          text: '',
                          align: 'right',
                          verticalAlign: 'top',
                          y: -5,
                          style: {
                              fontWeight: 'bold',
                              color: 'black',
                          },
                      },
                  }
                : {},

            ...sortedPlotLines.map((x: any, index) => {
                const label = x[0];
                const value = x[1];
                const text = ` ${label}  ${this.state.plotLineValues ? `${value.toFixed(decimals)} ${unit}` : ''}`;

                // 👉 Compare with NEXT value instead of previous
                const next = sortedPlotLines[index + 1];
                const isClose = next && Math.abs(next[1] - value) < 50;

                const baseOffset = text.length * 6;

                return {
                    zIndex: 5,
                    value,
                    color: 'red',
                    width: 1,
                    label: {
                        useHTML: true,
                        text: `<div style="background-color: rgba(255, 255, 255, 0.7);">${text}</div>`,
                        align: 'right',
                        verticalAlign: 'top',
                        y: baseOffset,
                        x: isClose ? -12 : 2, // move right if close, left otherwise
                        style: { color: 'red' },
                    },
                };
            }),
        ];
    };

    createPolygon = (fixedMaxY) => {
        const minX = this.props.data[0].plotLines.find((y) => {
            return y[0].includes('25%');
        })[1];
        const maxX = this.props.data[0].plotLines.find((y) => {
            return y[0].includes('75%');
        })[1];

        const { maxY, minY } = this.findMinMaxY();

        return [
            { x: minX, y: minY },
            { x: minX, y: fixedMaxY },
            { x: maxX, y: fixedMaxY },
            { x: maxX, y: minY },
            { x: minX, y: minY },
        ];
    };

    render(): JSX.Element {
        return (
            <div className="relative ">
                <HighchartsReact ref={this.chartRef} highcharts={Highcharts} options={this.state.options} />
            </div>
        );
    }
}

export default ProfileHistogramChart;
